import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { sendEmail } from '../utils/email';
import { trackEvent } from '../utils/metaPixel';

interface LeadFormProps {
  title?: string;
  subtitle?: string;
  className?: string;
  showPromo?: boolean;
  ctaText?: string;
  onSuccess?: () => void;
}

export default function LeadForm({ 
  title = "START YOUR FITNESS JOURNEY", 
  subtitle = "Get started today", 
  className = "", 
  showPromo = false,
  ctaText = "GET STARTED NOW",
  onSuccess
}: LeadFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interests: [] as string[]
  });

  const interests = [
    'Personal Training',
    'Group Classes',
    'Recovery Zone',
    'Swimming Pool',
    'Membership Information',
    'General Inquiry'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.name.trim() || !formData.email.trim()) {
        toast.error('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        setIsSubmitting(false);
        return;
      }

      await sendEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: 'Lead Form Submission',
        interests: formData.interests
      });

      // Track lead form submission in Meta Pixel
      trackEvent('Lead', {
        content_name: 'Lead Form Submission',
        content_category: formData.interests.join(', ') || 'General Inquiry'
      });
      
      toast.success('Thank you! Our team will contact you shortly.');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        interests: []
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInterestChange = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  return (
    <div className={`bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-blue-500/10 ${className}`}>
      <div className="text-center mb-6">
        <h3 className="text-2xl sm:text-3xl font-bold mb-2 text-white">{title}</h3>
        <p className="text-gray-400">{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2 text-white">Your Name *</label>
          <input
            type="text"
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-white placeholder-gray-400"
            placeholder="Enter your name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2 text-white">Email Address *</label>
          <input
            type="email"
            id="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-white placeholder-gray-400"
            placeholder="Enter your email"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium mb-2 text-white">Phone Number</label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-white placeholder-gray-400"
            placeholder="Enter your phone number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-white">I'm Interested In</label>
          <div className="grid grid-cols-2 gap-2">
            {interests.map((interest) => (
              <label
                key={interest}
                className="flex items-center space-x-2 cursor-pointer group"
              >
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.interests.includes(interest)}
                    onChange={() => handleInterestChange(interest)}
                    className="peer sr-only"
                  />
                  <div className="w-4 h-4 border-2 border-blue-400 rounded bg-transparent peer-checked:bg-blue-400 peer-checked:border-blue-400 transition-all duration-200"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity duration-200">
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
                <span className="text-sm text-gray-300 group-hover:text-blue-400 transition-colors duration-200">{interest}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-xl font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>{ctaText}</span>
            </>
          )}
        </button>

        <p className="text-center text-sm text-gray-400 mt-4">
          By submitting this form, you agree to our terms and privacy policy.
        </p>
      </form>
    </div>
  );
}